import {
  call,
  put,
  select,
  takeEvery,
  all,
  take,
} from "typed-redux-saga/macro";
import { callApi } from "../../utils/SagaUtils";
import {
  CREATE_PRODUCT_BADGE,
  CreateProductBadgeAction,
  DELETE_BADGE,
  DeleteBadgeAction,
  FETCH_PRODUCT_BADGES_LIST,
  GET_PRODUCT_BADGE,
  GetBadgeAction,
  LOAD_PRODUCT_BADGES_LIST,
  LoadProductBadgeListAction,
  LoadQueryBadgeAction,
  QUERY_PRODUCT_BADGE,
  UPDATE_BADGE_PRECEDENCE,
  UPDATE_PRODUCT_BADGE,
  UpdateBadgePrecedenceAction,
  UpdateProductBadgeAction,
  QUERY_PRODUCT_BADGE_URL,
  UPDATE_PRODUCT_BADGE_ASSIGNMENTS,
  SEQUENTIAL_BADGE_ASSIGNMENT,
  UpdateProductBadgeAssignmentsAction,
  SequentialBadgeAssignmentAction,
  badges,
  BADGE_CONFIGURE_MESSAGE,
} from "./ProductBadgeTypes";
import {
  deleteData,
  getData,
  patchData,
  postData,
  putData,
} from "../../services/ApiService";
import { acquireEndpoint } from "../../utils/SmartMerchandiserAPI";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "../store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import { addGlobalAlertState } from "store/global-alert/GlobalAlertActions";
import { setProductBadgeAssignments } from "./ProductBadgeActions";
import { selectProductIds } from "store/product-list/ProductListSelectors";
import {
  selectProductBadgeAssignmentsList,
  selectProductBadgeList,
} from "./ProductSelectors";
import { selectOverlay } from "../overlay/OverlaySelectors";
import { selectCurrentCategory } from "../category/CategorySelectors";
import { OVERLAY_TABS } from "../../utils/Constants";
import { UPDATE_SELECTED_OVERLAY, updateSelectedOverlayAction } from "../overlay/OverlayTypes";
import { selectProducts } from "../product/ProductSelectors";
import { selectGetProductIds } from "../product-list/ProductListSelectors";

function* loadProductBadgeList(action: LoadProductBadgeListAction) {
  const actionType = FETCH_PRODUCT_BADGES_LIST;
  const constName = Object.keys({ FETCH_PRODUCT_BADGES_LIST })[0].toString();

  try {
    const endpoint = acquireEndpoint(constName);
    const result = yield* call(callApi, actionType, getData, null, endpoint);
    
    // Check conditions after successful response and dispatch badge configure message if needed
    if (result && result.type === actionType.SUCCESS && action.payload.isFromCategorySelection) {
      const selectedOverlay = yield* select(selectOverlay);
      const currentCategory = yield* select(selectCurrentCategory);
      const currentStoreId = yield* select(selectCurrentStoreId);
      const badgesList = result.payload?.badges ?? [];
      
      // Filter badges by current store
      const filteredBadgesList = badgesList.filter(
        (badge) => badge.storeId === currentStoreId || badge.storeId === null,
      );
      
      if (
        selectedOverlay === OVERLAY_TABS.PRODUCTS_BADGES &&
        filteredBadgesList.length === 0 &&
        currentCategory
      ) {
        yield* call(badgeConfigureMessage);
      }
    }
  } catch (e: any) {
    console.error(e);
    yield* put({ type: FETCH_PRODUCT_BADGES_LIST.FAILURE, message: e.message });
  }
}

export function* fetchProductQueryProduct(action: LoadQueryBadgeAction) {
  try {
    const actionType = QUERY_PRODUCT_BADGE;
    const storeId = yield* select(selectCurrentStoreId);
    const catalogId = yield* select(selectCurrentCatalogId);
    const localeCode = yield* select(selectCurrentLocale);
    const constName = Object.keys({ QUERY_PRODUCT_BADGE_URL })[0].toString();
    const endpoint = acquireEndpoint(constName);
    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };

    yield* call(
      callApi,
      actionType,
      postData,
      action.payload,
      endpoint,
      headersObj,
    );
  } catch (e: any) {
    console.error(e);
    yield* put({ type: QUERY_PRODUCT_BADGE.FAILURE, message: e.message });
  }
}

function* updateBadgePrecedence(action: UpdateBadgePrecedenceAction) {
  try {
    const actionType = UPDATE_BADGE_PRECEDENCE;
    const storeId =
      action.payload.storeId ?? (yield* select(selectCurrentStoreId));
    const constName = Object.keys({
      UPDATE_BADGE_PRECEDENCE,
    })[0].toString();
    const badgeId = action.payload.badgeId;
    const endpoint = acquireEndpoint(constName, badgeId);
    const headersObj = {
      "x-store-id": storeId,
    };

    yield* call(
      callApi,
      actionType,
      patchData,
      action.payload,
      endpoint,
      headersObj,
    );
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: UPDATE_BADGE_PRECEDENCE.FAILURE,
      message: e.message,
    });
  }
}

function* deleteBadge(action: DeleteBadgeAction) {
  try {
    const actionType = DELETE_BADGE;
    const constName = Object.keys({ DELETE_BADGE })[0].toString();
    const { badgeId } = action.payload;
    const storeId =
      action.payload.storeId ?? (yield* select(selectCurrentStoreId));
    const endpoint = acquireEndpoint(constName, badgeId);
    const headersObj = {
      "x-store-id": storeId,
    };

    const result = yield* call(
      callApi,
      actionType,
      deleteData,
      null,
      endpoint,
      headersObj,
    );
    if (result && result.type === actionType.SUCCESS) {
      yield* put(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "productBadgeOperations.badgeDeleteSuccess",
                defaultMessage: "Badge deleted successfully",
              },
              severity: "success",
              variant: "standard",
            },
          ],
        }),
      );
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: DELETE_BADGE.FAILURE,
      message: e.message,
    });
  }
}

function* getProductBadge(action: GetBadgeAction) {
  try {
    const actionType = GET_PRODUCT_BADGE;
    const constName = Object.keys({ GET_PRODUCT_BADGE })[0].toString();
    const { badgeId } = action.payload;
    const storeId =
      action.payload.storeId ?? (yield* select(selectCurrentStoreId));
    const endpoint = acquireEndpoint(constName, badgeId);
    const headersObj = {
      "x-store-id": storeId,
    };

    yield* call(callApi, actionType, getData, null, endpoint, headersObj);
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: GET_PRODUCT_BADGE.FAILURE,
      message: e.message,
    });
  }
}

function* createProductBadge(action: CreateProductBadgeAction) {
  try {
    const actionType = CREATE_PRODUCT_BADGE;
    const constName = Object.keys({ CREATE_PRODUCT_BADGE })[0].toString();
    const currentStoreId = yield* select(selectCurrentStoreId);
    const storeId = action.payload.storeId ?? currentStoreId;
    const endpoint = acquireEndpoint(constName);
    const headersObj = {
      "x-store-id": storeId,
    };
    const productIdsArray = yield* select(selectProductIds);

    const result = yield* call(
      callApi,
      actionType,
      postData,
      action.payload,
      endpoint,
      headersObj,
    );
    if (result && result.type === actionType.SUCCESS) {
      yield* put(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "productBadgeOperations.badgeCreateSuccess",
                defaultMessage: "Badge Created successfully",
              },
              severity: "success",
              variant: "standard",
            },
          ],
        }),
      );
      if (currentStoreId) {
        yield* put({
          type: QUERY_PRODUCT_BADGE.REQUEST,
          payload: {
            productIds: productIdsArray,
          },
        });
      }
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: CREATE_PRODUCT_BADGE.FAILURE,
      message: e.message,
    });
  }
}

function* updateProductBadge(action: UpdateProductBadgeAction) {
  try {
    const actionType = UPDATE_PRODUCT_BADGE;
    const constName = Object.keys({ UPDATE_PRODUCT_BADGE })[0].toString();
    const storeId =
      action.payload.storeId ?? (yield* select(selectCurrentStoreId));
    const { badgeId } = action.payload;
    const endpoint = acquireEndpoint(constName, badgeId);
    const headersObj = {
      "x-store-id": storeId,
    };

    const result = yield* call(
      callApi,
      actionType,
      patchData,
      action.payload,
      endpoint,
      headersObj,
    );
    if (result && result.type === actionType.SUCCESS) {
      yield* put(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "productBadgeOperations.badgeUpdateSuccess",
                defaultMessage: "Badge updated successfully",
              },
              severity: "success",
              variant: "standard",
            },
          ],
        }),
      );
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: UPDATE_PRODUCT_BADGE.FAILURE,
      message: e.message,
    });
  }
}

function* updateProductBadgeAssignments(
  action: UpdateProductBadgeAssignmentsAction,
) {
  try {
    const actionType = UPDATE_PRODUCT_BADGE_ASSIGNMENTS;
    const constName = Object.keys({
      UPDATE_PRODUCT_BADGE_ASSIGNMENTS,
    })[0].toString();
    const storeId = yield* select(selectCurrentStoreId);
    const localeCode = yield* select(selectCurrentLocale);
    const { productId, badgeIds, assigned } = action.payload;

    // Create an array of API calls for each badge ID
    const apiCalls = badgeIds.map((badgeId) => {
      const endpoint = acquireEndpoint(constName, productId, badgeId);
      const headersObj = {
        "x-store-id": storeId,
        "x-locale-code": localeCode,
      };

      return call(
        callApi,
        actionType,
        putData,
        { assigned },
        endpoint,
        headersObj,
      );
    });

    // Execute all API calls in parallel and store results
    const results = yield* all(apiCalls);

    // Filter successful results and create payload
    const successfulResults = results.filter(
      (result) => result?.type === actionType.SUCCESS,
    );
    if (successfulResults.length > 0) {
      const badges: badges[] = [];
      successfulResults.forEach((result) => {
        result.payload.badges?.forEach((badge: badges) => {
          badges.push(badge);
        });
      });
      yield* put(
        setProductBadgeAssignments({
          productId,
          badges: badges,
        }),
      );
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: UPDATE_PRODUCT_BADGE_ASSIGNMENTS.FAILURE,
      message: e.message,
    });
  }
}

function* sequentialBadgeAssignment(action: SequentialBadgeAssignmentAction) {
  try {
    const { productId, badgeId } = action.payload;
    const badgeAssignmentsList = yield* select(
      selectProductBadgeAssignmentsList,
      productId,
    );
    const existingBadgeIds =
      badgeAssignmentsList?.map((assignment) => assignment.badgeId) || [];

    // Step 1: Remove all existing badges
    if (existingBadgeIds.length > 0) {
      yield* put({
        type: UPDATE_PRODUCT_BADGE_ASSIGNMENTS.REQUEST,
        payload: {
          assigned: false,
          productId,
          badgeIds: existingBadgeIds,
        },
      });

      // Wait for the remove operation to complete
      yield* take(UPDATE_PRODUCT_BADGE_ASSIGNMENTS.SUCCESS);
    }

    // Step 2: Add the new badge
    if (
      badgeAssignmentsList?.length === 0 ||
      badgeAssignmentsList?.find((assignment) => assignment.badgeId !== badgeId)
    ) {
      yield* put({
        type: UPDATE_PRODUCT_BADGE_ASSIGNMENTS.REQUEST,
        payload: {
          assigned: true,
          productId,
          badgeIds: [badgeId],
        },
      });

      // Wait for the add operation to complete
      yield* take(UPDATE_PRODUCT_BADGE_ASSIGNMENTS.SUCCESS);
    }

    // Dispatch success action
    yield* put({
      type: SEQUENTIAL_BADGE_ASSIGNMENT.SUCCESS,
      payload: { productId, badgeId },
    });
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: SEQUENTIAL_BADGE_ASSIGNMENT.FAILURE,
      message: e.message,
    });
  }
}

function* badgeConfigureMessage() {
  yield* put(
    addGlobalAlertState({
      alertsProps: [
        {
          descriptor: {
            id: "productBadgeOperations.badgeConfigure",
            defaultMessage:
              "Please select Badge Management from the Settings menu to configure badges",
          },
          severity: "warning",
          variant: "standard",
        },
      ],
    }),
  );
}


function* handleOverlayChange(action: updateSelectedOverlayAction) {
  // When badge overlay is opened, check if products are loaded and badges are empty
  if (action.overlay === OVERLAY_TABS.PRODUCTS_BADGES) {
    const products = yield* select(selectProducts);
    const productIds = yield* select(selectGetProductIds);
    const currentCategory = yield* select(selectCurrentCategory);
    const currentStoreId = yield* select(selectCurrentStoreId);
    const badgesList = yield* select(selectProductBadgeList);
    
    // Check if products are already loaded
    const hasProducts = productIds.length > 0 && Object.keys(products).length > 0;
    
    if (hasProducts && currentCategory) {
      // Filter badges by current store
      const filteredBadgesList = badgesList?.filter(
        (badge) => badge.storeId === currentStoreId || badge.storeId === null,
      ) ?? [];
      
      // If badges list is empty, show configure message
      if (filteredBadgesList.length === 0) {
        yield* call(badgeConfigureMessage);
      }
    }
  }
}

export function* watchLoadQueryProduct() {
  yield* takeEvery(QUERY_PRODUCT_BADGE.REQUEST, fetchProductQueryProduct);
}

export function* watchLoadProductBadgeList() {
  yield* takeEvery(LOAD_PRODUCT_BADGES_LIST, loadProductBadgeList);
}

export function* watchUpdateBadgePrecedence() {
  yield* takeEvery(UPDATE_BADGE_PRECEDENCE.REQUEST, updateBadgePrecedence);
}

export function* watchDeleteBadge() {
  yield* takeEvery(DELETE_BADGE.REQUEST, deleteBadge);
}

export function* watchGetProductBadge() {
  yield* takeEvery(GET_PRODUCT_BADGE.REQUEST, getProductBadge);
}

export function* watchCreateProductBadge() {
  yield* takeEvery(CREATE_PRODUCT_BADGE.REQUEST, createProductBadge);
}

export function* watchUpdateProductBadge() {
  yield* takeEvery(UPDATE_PRODUCT_BADGE.REQUEST, updateProductBadge);
}

export function* watchUpdateProductBadgeAssignments() {
  yield* takeEvery(
    UPDATE_PRODUCT_BADGE_ASSIGNMENTS.REQUEST,
    updateProductBadgeAssignments,
  );
}

export function* watchSequentialBadgeAssignment() {
  yield* takeEvery(
    SEQUENTIAL_BADGE_ASSIGNMENT.REQUEST,
    sequentialBadgeAssignment,
  );
}

export function* watchBadgeConfigureMessage() {
  yield* takeEvery(BADGE_CONFIGURE_MESSAGE, badgeConfigureMessage);
}

export function* watchOverlayChange() {
  yield* takeEvery(UPDATE_SELECTED_OVERLAY, handleOverlayChange);
}
